import React, { FC, useEffect } from 'react';
import { Breadcrumb, Button, Col, Row, Skeleton } from 'antd';
import { RouteComponentProps } from 'react-router-dom';
import { CommonLayout } from 'common/components/Layouts/CommonLayout';
import { ELayoutSize } from 'common/const/common.const';
import { ERoutesPrivate } from 'common/models/routesModel';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  addAnnouncementModel,
  getAnnouncementModel,
  setAnnouncementModel,
  updateAnnouncementModel,
  updateAnnouncementVisibility,
} from 'app/store/reducers/announcement.reducer';
import { AnnouncementForm } from 'entities/Announcement/components/Form/AnnouncementForm';
import { AnnouncementFormSkeleton } from 'entities/Announcement/components/Skeleton/AnnouncementFormSkeleton';
import {
  EAnnouncementStatus,
  IAnnouncementCreateData,
  IAnnouncementUpdateData,
} from 'entities/Announcement/Announcement.models';

interface IParams {
  id?: string;
}

type AllProps = RouteComponentProps<IParams>;

export const AnnouncementPage: FC<AllProps> = ({ match }) => {
  const dispatch = useAppDispatch();

  const { announcementModel, announcementModelLoading } = useAppSelector((state) => state.announcement);

  const { params } = match;
  const { id } = params;
  const announcementName = announcementModel?.name;
  const isEdit = !!id;

  useEffect(() => {
    const { params } = match;
    const { id } = params;

    if (id) {
      dispatch(getAnnouncementModel(id));
    }

    // Braces matter: a cleanup callback must return void, not the dispatched action.
    return () => {
      dispatch(setAnnouncementModel(null));
    };
  }, []);

  const createAnnouncement = (data: IAnnouncementCreateData) => {
    dispatch(addAnnouncementModel(data));
  };

  const editAnnouncement = (data: IAnnouncementUpdateData) => {
    dispatch(updateAnnouncementModel(data));
  };

  return (
    <CommonLayout typeLayout={ELayoutSize.Large} classStyle="mt-10 pb-10">
      <Col span={16}>
        <Row justify="space-between" align="middle" className="mb-8">
          <Breadcrumb className="fs-sm font-weight-5 font-family__default">
            <Breadcrumb.Item href={ERoutesPrivate.Announcements}>Announcement list</Breadcrumb.Item>
            <Breadcrumb.Item>
              {isEdit ? (
                !announcementModelLoading ? (
                  announcementName
                ) : (
                  <Skeleton.Input active style={{ width: '200px' }} size="small" />
                )
              ) : (
                'Create new announcement'
              )}
            </Breadcrumb.Item>
          </Breadcrumb>
          {/* Unpublish pulls a live announcement back to Pending (hidden from customers, editable);
              Publish returns it to the feed. Expired can't be toggled — the run is over. */}
          {isEdit &&
            announcementModel &&
            (announcementModel.isHidden ? (
              // A hidden announcement that ran past its endDate is Expired (BE blocks the
              // toggle too) — the button stays visible but locked.
              <Button
                type="primary"
                loading={announcementModelLoading}
                disabled={announcementModel.status === EAnnouncementStatus.Expired}
                onClick={() => dispatch(updateAnnouncementVisibility(announcementModel.id, false))}
              >
                Publish
              </Button>
            ) : announcementModel.status === EAnnouncementStatus.Active ? (
              <Button
                danger
                loading={announcementModelLoading}
                onClick={() => dispatch(updateAnnouncementVisibility(announcementModel.id, true))}
              >
                Unpublish (hide & edit)
              </Button>
            ) : null)}
        </Row>
        {!announcementModelLoading || !isEdit ? (
          <AnnouncementForm
            announcementId={id}
            createAnnouncement={createAnnouncement}
            editAnnouncement={editAnnouncement}
          />
        ) : (
          <AnnouncementFormSkeleton />
        )}
      </Col>
    </CommonLayout>
  );
};
